import { useMemo } from "react";
import { useTranslate, useNavigation } from "@refinedev/core";
import { EditButton, NumberField } from "@refinedev/mui";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import CustomChip from "@/components/override/Chip";
import palette from "@/palette";
import { alpha } from "@mui/material";

type Props = {
  tableQuery: {
    data: {
      data: IProduct[];
      total: number;
    };
    isLoading?: boolean;
    isError?: boolean;
  };
  filters: any[];
  setFilters: (filters: any[]) => void;
  current: number;
  setCurrent: (current: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
};

export const ProductListCard = (props: Props) => {
  const t = useTranslate();
  const rawProducts = props.tableQuery?.data?.data || [];
  const materialFilters = useMemo(() => {
    const filter = props.filters.find((filter) =>
      "field" in filter && filter.field === "material"
    );
    return (filter?.value || []) as string[];
  }, [props.filters]);

  // Client-side filtering fallback
  const filteredProducts = useMemo(() => {
    if (!materialFilters.length) return rawProducts;
    return rawProducts.filter((product: IProduct) =>
      materialFilters.some(material => product.material?.toLowerCase().includes(material.toLowerCase()))
    );
  }, [rawProducts, materialFilters]);

  // Pagination logic
  const paginatedProducts = useMemo(() => {
    const startIndex = (props.current - 1) * props.pageSize;
    const endIndex = startIndex + props.pageSize;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, props.current, props.pageSize]);

  const handleMaterialToggle = (material: string) => {
    const newFilters = materialFilters.includes(material)
      ? materialFilters.filter(m => m !== material)
      : [...materialFilters, material];

    props.setFilters([{
      field: "material",
      operator: "contains",
      value: newFilters,
    }]);
    props.setCurrent(1);
  };

  const navigate = useNavigation();

  const allMaterials: string[] = useMemo(() => {
    return Array.from(new Set(rawProducts?.map((product: IProduct) => 
      ((product.material?.charAt(0).toUpperCase() || "") + (product.material?.slice(1)?.toLowerCase() || ""))
    ) || []));
  }, [rawProducts]);

  return (
    <>
      <Divider />
      <Stack direction="row" spacing="12px" py="16px">
        <CustomChip
          label={`${t("products.filter.allMaterials.label")}`}
          type="tag"
          status={materialFilters.length ? "positive" : "neutral"}
          onClick={() => {
            props.setFilters([{
              field: "material",
              operator: "contains",
              value: [],
            }]);
            props.setCurrent(1);
          }}
          sx={{
            color: `${materialFilters.length ? palette.dark.primary.main : "white"} !important`,
            backgroundColor: `${materialFilters.length ? "transparent" : palette.dark.primary.main} !important`,
            '&:hover': {
              color: `${palette.dark.primary.main} !important`,
              backgroundColor: `${alpha(palette.dark.primary.main, 0.2)} !important`,
            },
          }}
        />
        {allMaterials.map((material: string) => (
          <CustomChip
            key={material}
            label={material}
            status={materialFilters.includes(material) ? "positive" : "neutral"}
            type="tag"
            onClick={() => handleMaterialToggle(material)}
            sx={{
              color: `${materialFilters.includes(material) ? "white" : palette.dark.primary.main} !important`,
              backgroundColor: `${materialFilters.includes(material) ? palette.dark.primary.main : "transparent"} !important`,
              '&:hover': {
                color: `${materialFilters.includes(material) ? "white" : palette.dark.primary.main} !important`,
                backgroundColor: `${materialFilters.includes(material) ? palette.dark.primary.main : "transparent"} !important`,
              },
            }}
          />
        ))}
      </Stack>
      <Divider />

      <Grid
        container
        spacing={3}
        sx={{
          marginTop: "24px",
        }}
      >
        {paginatedProducts?.map((product: IProduct) => {
          return (
            <Grid
              key={product.id}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 4,
              }}
            >
              <Card
                sx={{
                  height: "100%",
                }}
              >
                <CardActionArea
                  onClick={() => {
                    navigate.show("products", product.id);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "normal",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      "&:hover": {
                        ".edit-button": {
                          display: "flex",
                        },
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="250"
                      image={product.medias?.[0]?.cdn_url}
                      alt={product.name}
                      sx={{ objectFit: 'contain' }}
                    />
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <EditButton
                        className="edit-button"
                        resource="products"
                        recordItemId={product.id}
                        color="primary"
                        variant="contained"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          display: "none",
                          zIndex: 1,
                        }}
                      />
                    </Box>
                  </Box>

                  <CardContent>
                    <Stack
                      mb="8px"
                      direction="row"
                      justifyContent="space-between"
                      spacing="12px"
                    >
                      <Stack direction="column" spacing="12px">
                        <Typography variant="body1" fontWeight={500}>
                          {product.name}
                        </Typography>
                      <CustomChip
                        label={product.material || ""}
                        status="neutral"
                        type="tag"
                      />
                      </Stack>
                      <Stack direction="column" spacing="12px">
                        <NumberField
                          variant="body1"
                          value={product.cost_price || 0}
                          options={{
                            style: "currency",
                            currency: "INR",
                          }}
                        />
                        <CustomChip 
                          label={product.status} 
                          status={
                            product.status === "active" ? "positive" : 
                            product.status === "archived" ? "negative" : "neutral"
                          } 
                          type="status" 
                        />
                      </Stack>
                    </Stack>
                    {product.description && (
                      <Typography 
                        color="text.secondary" 
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Typography component="span" variant="body2" color="text.secondary" fontWeight="bold">Description: </Typography>
                        {product.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Divider
        sx={{
          marginTop: "24px",
        }}
      />
      <TablePagination
        component="div"
        count={filteredProducts.length}
        page={props.current ? props.current - 1 : 0}
        rowsPerPage={props.pageSize || 12}
        rowsPerPageOptions={[12, 24, 48, 96]}
        onRowsPerPageChange={(e) => {
          props.setPageSize(+e.target.value);
          props.setCurrent(1);  // Reset to first page when changing page size
        }}
        onPageChange={(_e, page) => {
          props.setCurrent(page + 1);
        }}
      />
    </>
  );
};